import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StoreIcon from "@mui/icons-material/Store";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DataTable from "react-data-table-component";
import { OrderServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { Loader } from "../../components/CustomLoader";
import CustomPagination from "../../components/CustomPagination";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TableFilter from "../../components/shared/TableFilter";
import Swal from "sweetalert2";
import { capitalize } from "../../utils/helpers";

// Status options for filter (TableFilter format)
const ORDER_STATUS_OPTIONS = [
  { label: "Pending Payment", value: "Pending Payment" },
  { label: "Payment Completed", value: "Payment Completed" },
  { label: "Ready for Pickup", value: "Ready for Pickup" },
  { label: "In Delivery", value: "In Delivery" },
  { label: "Completed", value: "Completed" },
  { label: "Cancelled", value: "Cancelled" },
];

// Status options for dropdown (without "All Status")
const ORDER_STATUS_DROPDOWN_OPTIONS = [
  "Pending Payment",
  "Payment Completed",
  "Ready for Pickup",
  "In Delivery",
  "Completed",
  "Cancelled",
];

// Auction type options for filter (TableFilter format)
const AUCTION_TYPE_OPTIONS = [
  { label: "Normal", value: "normal" },
  { label: "Market Drop AI", value: "marketDropAi" },
  { label: "Claim Point", value: "claimPoint" },
];

// Sort options
const SORT_OPTIONS = [
  { label: "Default", value: "" },
  { label: "Newest First", value: "newest" },
  { label: "Oldest First", value: "oldest" },
  { label: "Price: Low to High", value: "priceAsc" },
  { label: "Price: High to Low", value: "priceDesc" },
];



// Auction type chip colors
const getAuctionTypeColor = (type) => {
  switch (type) {
    case "normal":
      return "default";
    case "marketDropAi":
      return "primary";
    case "claimPoint":
      return "secondary";
    default:
      return "default";
  }
};

// Format auction type label
const formatAuctionType = (type) => {
  const labels = {
    normal: "Normal",
    marketDropAi: "Market Drop AI",
    claimPoint: "Claim Point",
  };
  return labels[type] || type;
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Format price
const formatPrice = (price) => {
  if (!price && price !== 0) return "-";
  return `$${price.toLocaleString()}`;
};

const OrdersList = () => {
  const navigate = useNavigate();

  // Data states
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [auctionId, setAuctionId] = useState("");
  const [orderStatus, setOrderStatus] = useState([]);
  const [auctionType, setAuctionType] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("");

  // Delivery Type Dialog states
  const [deliveryDialog, setDeliveryDialog] = useState({
    open: false,
    orderId: null,
    deliveryType: "",
    estimationDate: "",
    shippingCharge: "",
  });

  const fetchOrders = async (search = searchTerm) => {
    const body = {
      pageNumber: currentPage,
      pageSize: pageSize,
      searchTerm: search || "",
      auctionId: auctionId || "",
      orderStatus: Array.isArray(orderStatus) ? orderStatus : orderStatus || "",
      auctionType: Array.isArray(auctionType) ? auctionType : auctionType || "",
      dateFrom: dateFrom || "",
      dateTo: dateTo || "",
      sortBy: sortBy || "",
    };

    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await OrderServices.getAllOrders(body);

      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data || []);
          setTotalPages(res?.data?.pagination?.totalPages || 0);
          setTotalRecords(res?.data?.pagination?.totalRecords || 0);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData([]);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchOrders(query), 400),
    [
      currentPage,
      pageSize,
      orderStatus,
      auctionType,
      dateFrom,
      dateTo,
      sortBy,
      auctionId,
    ]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchTerm(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handleStatusChange = async (orderId, newStatus, currentDeliveryType, currentStatus) => {
    // If trying to set status to "Completed"
    if (newStatus === "Completed") {
      // Check if delivery type is empty
      if (!currentDeliveryType || currentDeliveryType === "") {
        toast.error("Please select delivery type first before setting status to Completed");
        return;
      }

      // If already completed, prevent change
      if (currentStatus === "Completed") {
        toast.error("Cannot change status once order is completed");
        return;
      }

      // Show confirmation dialog
      const result = await Swal.fire({
        text: "Are you sure you want to mark this order as completed? You will not be able to change the status or delivery type afterwards.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00a1da",
        confirmButtonText: "Yes, Complete",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    try {
      const res = await OrderServices.updateOrderStatus({
        orderId,
        status: newStatus,
      });
      if (res.status === 200) {
        toast.success(res?.data?.message || "Order status updated successfully");
        fetchOrders(searchTerm);
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  const handleDeliveryTypeClick = (row) => {
    // Prevent opening dialog if status is Completed
    if (row.status === "Completed") {
      toast.error("Cannot change delivery type once order is completed");
      return;
    }
    
    // Format estimatedDelivery date for date input (YYYY-MM-DD)
    let formattedDate = "";
    if (row.estimatedDelivery || row.estimationDate) {
      const date = new Date(row.estimatedDelivery || row.estimationDate);
      if (!isNaN(date.getTime())) {
        formattedDate = date.toISOString().split("T")[0];
      }
    }
    
    setDeliveryDialog({
      open: true,
      orderId: row._id || row.orderId,
      deliveryType: row.deliveryType || "",
      estimationDate: formattedDate,
      shippingCharge: row.shippingCharge || "",
    });
  };

  const handleDeliveryTypeChange = (newType) => {
    setDeliveryDialog((prev) => ({
      ...prev,
      deliveryType: newType,
      estimationDate: newType === "Pickup" ? "" : prev.estimationDate,
    }));
  };

  const handleSaveDeliveryType = async () => {
    const { orderId, deliveryType, estimationDate, shippingCharge } = deliveryDialog;

    if (deliveryType === "delivery" && !estimationDate) {
      toast.error("Please select an estimation date for delivery");
      return;
    }

    try {
      const payload = {
        orderId,
        deliveryType,
        shippingCharge: Number(shippingCharge) || 0,
      };
      if (deliveryType === "delivery" && estimationDate) {

        const date = new Date(estimationDate);
        date.setHours(23, 59, 59, 999);
        payload.estimatedDelivery = date.toISOString();
      }

      const res = await OrderServices.updateOrderStatus(payload);
      if (res.status === 200) {
        toast.success(res?.data?.message || "Delivery type updated successfully");
        setDeliveryDialog({ open: false, orderId: null, deliveryType: "", estimationDate: "", shippingCharge: "" });
        fetchOrders(searchTerm);
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  const handleCloseDeliveryDialog = () => {
    setDeliveryDialog({ open: false, orderId: null, deliveryType: "", estimationDate: "", shippingCharge: "" });
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setAuctionId("");
    setOrderStatus([]);
    setAuctionType([]);
    setDateFrom("");
    setDateTo("");
    setSortBy("");
    setCurrentPage(1);
  };

  // Check if any filter is active
  const isFilterActive =
    searchTerm ||
    auctionId ||
    (Array.isArray(orderStatus) ? orderStatus.length > 0 : orderStatus) ||
    (Array.isArray(auctionType) ? auctionType.length > 0 : auctionType) ||
    dateFrom ||
    dateTo ||
    sortBy;

  useEffect(() => {
    fetchOrders(searchTerm);
  }, [
    currentPage,
    pageSize,
    orderStatus,
    auctionType,
    dateFrom,
    dateTo,
    sortBy,
    auctionId,
  ]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "80px",
    },
    {
      name: "Buyer Name",
      sortable: true,
      selector: (row) => row.buyerName,
    },
    {
      name: "Vehicle Name",
      sortable: true,
      cell: (row) => (
        <Box>
          <Typography variant="body3">
            {row.vehicleName}
          </Typography>
        
        </Box>
      ),
    },
    {
      name: "Auction Type",
      sortable: true,
      cell: (row) => (
        <Chip
          label={formatAuctionType(row.auctionType)}
          color={getAuctionTypeColor(row.auctionType)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      name: "Delivery Type",
      sortable: false,
      cell: (row) => {
        const currentType = row.deliveryType || "";
        const isCompleted = row.status === "Completed";
        return (
          <Box
            onClick={() => !isCompleted && handleDeliveryTypeClick(row)}
            sx={{
              cursor: isCompleted ? "not-allowed" : "pointer",
              width: "100%",
              opacity: isCompleted ? 0.6 : 1,
            }}
          >
            <FormControl size="small" fullWidth disabled={isCompleted}>
              <Select
                value={capitalize(currentType) || ""}
                displayEmpty
                readOnly
                renderValue={(value) => {
                  if (!value) return "Select Type";
                  return value;
                }}
              >
                <MenuItem value="delivery">Delivery</MenuItem>
                <MenuItem value="Pickup">Pickup</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
      },
      width: "180px",
    },
    {
      name: "Date",
      sortable: false,
      selector: (row) => row.estimatedDelivery || row.estimationDate,
      cell: (row) => {
        const estimatedDate = row.estimatedDelivery || row.estimationDate;
        return estimatedDate ? formatDate(estimatedDate) : "";
      },
    },
    {
      name: "Final Price",
      sortable: true,
      selector: (row) => row.finalPrice,
      cell: (row) => (
        <span style={{ fontWeight: 600, color: "#2e7d32" }}>
          {formatPrice(row.finalPrice)}
        </span>
      ),
    },
    {
      name: "Order Date",
      sortable: true,
      selector: (row) => row.orderDate,
      cell: (row) => formatDate(row.orderDate),
    },
    {
      name: "Status",
      sortable: true,
      cell: (row) => {
        const isCompleted = row.status === "Completed";
        return (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={row.status || ""}
              onChange={(e) => handleStatusChange(
                row._id || row.orderId,
                e.target.value,
                row.deliveryType || "",
                row.status || ""
              )}
              disabled={isCompleted}
              sx={{
                opacity: isCompleted ? 0.6 : 1,
              }}
            >
              {ORDER_STATUS_DROPDOWN_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
   
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <Tooltip title="View Details" placement="top">
          <IconButton
            color="primary"
            onClick={() => navigate(`/ordermanagement/detail/${row._id}`)}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Orders Management"
        subheader={totalRecords > 0 ? `Total: ${totalRecords} orders` : ""}
      />

      {/* Filter Section */}
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="flex-end"
        alignItems="center"
        p={2}
        gap={2}
      >
        <TableFilter
          data={ORDER_STATUS_OPTIONS}
          value={orderStatus}
          setValue={(value) => {
            setOrderStatus(value);
            setCurrentPage(1);
          }}
          filterName="Order Status"
        />
        <TableFilter
          data={AUCTION_TYPE_OPTIONS}
          value={auctionType}
          setValue={(value) => {
            setAuctionType(value);
            setCurrentPage(1);
          }}
          filterName="Auction Type"
        />
        <TextField
          size="small"
          type="date"
          label="From Date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setCurrentPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          size="small"
          type="date"
          label="To Date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setCurrentPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <Input
          value={searchTerm}
          placeholder="Search orders"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  setSearchTerm("");
                  debouncedSearch("");
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        {isFilterActive && (
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            sx={{
              whiteSpace: "nowrap",
              minWidth: "100px",
            }}
          >
            Clear
          </Button>
        )}
      </Box>

      <CardContent>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
              },
            },
            rows: {
              style: {
                minHeight: "60px",
              },
            },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalRecords}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          onChangeRowsPerPage={(currentRowsPerPage) => {
            setPageSize(currentRowsPerPage);
            setCurrentPage(1);
          }}
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          paginationComponent={(props) => (
            <CustomPagination {...props} currentPage={currentPage} />
          )}
          className="custom"
          noDataComponent={
            <Box sx={{ p: 4, textAlign: "center" }}>
              <p>No orders found</p>
              {isFilterActive && (
                <Button
                  variant="text"
                  onClick={handleClearFilters}
                  sx={{ mt: 1 }}
                >
                  Clear filters and try again
                </Button>
              )}
            </Box>
          }
        />
      </CardContent>

      {/* Delivery Type Dialog */}
      <Dialog
        open={deliveryDialog.open}
        onClose={handleCloseDeliveryDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "delivery-dialog-paper",
        }}
      >
        <Box className="delivery-dialog-header">
          <Box className="delivery-dialog-circle-1" />
          <Box className="delivery-dialog-circle-2" />
          <DialogTitle className="delivery-dialog-title">
            Set Delivery Type
          </DialogTitle>
          <Box className="delivery-dialog-subtitle">
            Choose how the order will be delivered
          </Box>
        </Box>

        <DialogContent className="delivery-dialog-content">
          <Box className="delivery-dialog-form-container">
            {/* Delivery Type Selection */}
            <Box>
              <Box className="delivery-method-label">
                Select Delivery Method
              </Box>
              <Box className="delivery-options-container">
                <Box
                  onClick={() => handleDeliveryTypeChange("delivery")}
                  className={`delivery-option-card ${
                    deliveryDialog.deliveryType === "delivery" ? "selected" : ""
                  }`}
                >
                  <Box className="delivery-option-content">
                    <LocalShippingIcon className="delivery-option-icon" />
                    <Box className="delivery-option-title">Delivery</Box>
                    <Box className="delivery-option-description">
                      We'll deliver to your address
                    </Box>
                  </Box>
                </Box>

                <Box
                  onClick={() => handleDeliveryTypeChange("Pickup")}
                  className={`delivery-option-card ${
                    deliveryDialog.deliveryType === "Pickup" ? "selected" : ""
                  }`}
                >
                  <Box className="delivery-option-content">
                    <StoreIcon className="delivery-option-icon" />
                    <Box className="delivery-option-title">Pickup</Box>
                    <Box className="delivery-option-description">
                      Collect from our store
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Estimation Date - Only for Delivery */}
            {deliveryDialog.deliveryType === "delivery" && (
              <>
              <Box className="estimation-date-container">
                <Box className="estimation-date-label">
                  <CalendarTodayIcon className="estimation-date-icon" />
                  Estimation Date
                </Box>
                <TextField
                  fullWidth
                  type="date"
                  value={deliveryDialog.estimationDate}
                  onChange={(e) =>
                    setDeliveryDialog((prev) => ({
                      ...prev,
                      estimationDate: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().split("T")[0] }}
                  className="estimation-date-input"
                />
              </Box>
            

            {/* Shipping Charge */}
            <Box className="estimation-date-container" sx={{ mt: 2 }}>
              <Box className="estimation-date-label">
                Shipping Charge
              </Box>
              <TextField
                fullWidth
                type="number"
                value={deliveryDialog.shippingCharge}
                onChange={(e) =>
                  setDeliveryDialog((prev) => ({
                    ...prev,
                    shippingCharge: e.target.value,
                  }))
                }
                placeholder="Enter shipping charge"
                InputLabelProps={{ shrink: true }}
                inputProps={{ 
                  min: 0,
                  step: "0.01"
                }}
                className="estimation-date-input"
              />
            </Box>
            </>
            )}
          </Box>
        </DialogContent>

        <DialogActions className="delivery-dialog-actions">
          <Button
            onClick={handleCloseDeliveryDialog}
            variant="outlined"
            className="delivery-dialog-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveDeliveryType}
            variant="contained"
            disabled={!deliveryDialog.deliveryType}
            className="delivery-dialog-save-btn"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default OrdersList;
